import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Edit, Plus, Search, Trash2, Loader2, X } from "lucide-react";
import {
  useProductAttributes,
  useCreateProductAttribute,
  useUpdateProductAttribute,
  useDeleteProductAttribute,
} from "@/hooks/useProductAttributes";
import { FieldType, SpecificationSchema, SpecificationSchemaCreate, SpecificationSchemaUpdate } from "@/types/productAttributes";

const fieldTypeLabels = {
  [FieldType.SELECT]: "下拉選單",
  [FieldType.BOOLEAN]: "是/否",
  [FieldType.NUMBER]: "數字",
  [FieldType.TEXT]: "文字",
  [FieldType.DATE]: "日期",
  [FieldType.MULTISELECT]: "多選",
};

const fieldTypeColors = {
  [FieldType.SELECT]: "bg-blue-100 text-blue-800",
  [FieldType.BOOLEAN]: "bg-green-100 text-green-800",
  [FieldType.NUMBER]: "bg-purple-100 text-purple-800",
  [FieldType.TEXT]: "bg-gray-100 text-gray-800",
  [FieldType.DATE]: "bg-orange-100 text-orange-800",
  [FieldType.MULTISELECT]: "bg-indigo-100 text-indigo-800",
};

// Function to generate field name from display name
const generateFieldName = (displayName: string): string => {
  return displayName
    .toLowerCase()
    .replace(/\s+/g, '_')  // Replace spaces with underscores
    .replace(/[^\w_]/g, '') // Remove non-alphanumeric characters except underscores
    .replace(/_+/g, '_')    // Replace multiple underscores with single
    .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
};

export function ProductAttributes() {
  const { data: attributes = [], isLoading } = useProductAttributes();
  const createAttributeMutation = useCreateProductAttribute();
  const updateAttributeMutation = useUpdateProductAttribute();
  const deleteAttributeMutation = useDeleteProductAttribute();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newAttribute, setNewAttribute] = useState<SpecificationSchemaCreate>({
    displayName: "",
    fieldName: "",
    fieldType: FieldType.TEXT,
    options: null,
    isRequired: false,
  });
  const [newOptions, setNewOptions] = useState<string[]>([]);
  const [newOptionInput, setNewOptionInput] = useState("");
  const [isFieldNameManuallyEdited, setIsFieldNameManuallyEdited] = useState(false);
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<SpecificationSchema | null>(null);
  const [editAttribute, setEditAttribute] = useState<SpecificationSchemaUpdate>({});
  const [editOptions, setEditOptions] = useState<string[]>([]);
  const [editOptionInput, setEditOptionInput] = useState("");
  const [isEditFieldNameManuallyEdited, setIsEditFieldNameManuallyEdited] = useState(false);
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingAttributeId, setDeletingAttributeId] = useState<string | null>(null);

  const filteredAttributes = attributes.filter(
    (attr) =>
      attr.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      attr.fieldName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auto-generate field name from display name for new attributes
  useEffect(() => {
    if (!isFieldNameManuallyEdited && newAttribute.displayName) {
      setNewAttribute(prev => ({
        ...prev,
        fieldName: generateFieldName(prev.displayName)
      }));
    }
  }, [newAttribute.displayName, isFieldNameManuallyEdited]);

  // Auto-generate field name from display name for edit attributes
  useEffect(() => {
    if (!isEditFieldNameManuallyEdited && editAttribute.displayName) {
      setEditAttribute(prev => ({
        ...prev,
        fieldName: generateFieldName(prev.displayName || '')
      }));
    }
  }, [editAttribute.displayName, isEditFieldNameManuallyEdited]);

  const resetNewAttributeForm = () => {
    setNewAttribute({
      displayName: "",
      fieldName: "",
      fieldType: FieldType.TEXT,
      options: null,
      isRequired: false,
    });
    setNewOptions([]);
    setNewOptionInput("");
    setIsFieldNameManuallyEdited(false);
  };

  const handleCreateAttribute = async () => {
    if (!newAttribute.displayName.trim() || !newAttribute.fieldName.trim()) return;

    const attributeData: SpecificationSchemaCreate = {
      ...newAttribute,
      displayName: newAttribute.displayName.trim(),
      fieldName: newAttribute.fieldName.trim(),
      options: [FieldType.SELECT, FieldType.MULTISELECT].includes(newAttribute.fieldType) && newOptions.length > 0 
        ? newOptions 
        : null,
    };

    createAttributeMutation.mutate(attributeData, {
      onSuccess: () => {
        resetNewAttributeForm();
        setIsAddDialogOpen(false);
      },
    });
  };

  const handleEditClick = (attr: SpecificationSchema) => {
    setEditingAttribute(attr);
    setEditAttribute({
      displayName: attr.displayName,
      fieldName: attr.fieldName,
      fieldType: attr.fieldType,
      isRequired: attr.isRequired,
    });
    setEditOptions(attr.options || []);
    setEditOptionInput("");
    setIsEditFieldNameManuallyEdited(false);
    setIsEditDialogOpen(true);
  };

  const handleUpdateAttribute = async () => {
    if (!editingAttribute || !editAttribute.displayName?.trim() || !editAttribute.fieldName?.trim()) return;

    const updateData: SpecificationSchemaUpdate = {
      ...editAttribute,
      displayName: editAttribute.displayName?.trim(),
      fieldName: editAttribute.fieldName?.trim(),
      options: [FieldType.SELECT, FieldType.MULTISELECT].includes(editAttribute.fieldType!) && editOptions.length > 0 
        ? editOptions 
        : null,
    };

    updateAttributeMutation.mutate(
      { id: editingAttribute.id, data: updateData },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingAttribute(null);
        },
      }
    );
  };

  const handleDeleteClick = (attrId: string) => {
    setDeletingAttributeId(attrId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAttributeId) return;

    deleteAttributeMutation.mutate(deletingAttributeId, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingAttributeId(null);
      },
    });
  };

  const addNewOption = () => {
    if (newOptionInput.trim() && !newOptions.includes(newOptionInput.trim())) {
      setNewOptions([...newOptions, newOptionInput.trim()]);
      setNewOptionInput("");
    }
  };

  const removeNewOption = (index: number) => {
    setNewOptions(newOptions.filter((_, i) => i !== index));
  };

  const addEditOption = () => {
    if (editOptionInput.trim() && !editOptions.includes(editOptionInput.trim())) {
      setEditOptions([...editOptions, editOptionInput.trim()]);
      setEditOptionInput("");
    }
  };

  const removeEditOption = (index: number) => {
    setEditOptions(editOptions.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <h3 className="text-lg font-medium">產品屬性</h3>
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜尋屬性..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                新增屬性
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>新增產品屬性</DialogTitle>
                <DialogDescription>
                  建立新的產品屬性供條件式工作流程使用
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">顯示名稱</Label>
                  <Input
                    id="displayName"
                    value={newAttribute.displayName}
                    onChange={(e) => setNewAttribute({ ...newAttribute, displayName: e.target.value })}
                    placeholder="例如: 顏色、材質類型"
                  />
                </div>


                <div className="space-y-2">
                  <Label htmlFor="fieldType">欄位類型</Label>
                  <Select
                    value={newAttribute.fieldType}
                    onValueChange={(value: FieldType) => setNewAttribute({ ...newAttribute, fieldType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(fieldTypeLabels).map(([value, label]) => (
                        <SelectItem 
                          key={value} 
                          value={value}
                          className="hover:bg-accent hover:text-accent-foreground transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <div className={cn("w-2 h-2 rounded-full", fieldTypeColors[value as FieldType].split(' ')[0])} />
                            {label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {[FieldType.SELECT, FieldType.MULTISELECT].includes(newAttribute.fieldType) && (
                  <div className="space-y-2">
                    <Label>選項</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newOptionInput}
                        onChange={(e) => setNewOptionInput(e.target.value)}
                        placeholder="輸入選項"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addNewOption();
                          }
                        }}
                      />
                      <Button type="button" onClick={addNewOption}>
                        新增
                      </Button>
                    </div>
                    {newOptions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {newOptions.map((option, index) => (
                          <Badge key={index} variant="secondary" className="flex items-center gap-1">
                            {option}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() => removeNewOption(index)}
                            />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isRequired"
                    checked={newAttribute.isRequired}
                    onCheckedChange={(checked) => setNewAttribute({ ...newAttribute, isRequired: checked as boolean })}
                  />
                  <Label htmlFor="isRequired">必填欄位</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      resetNewAttributeForm();
                      setIsAddDialogOpen(false);
                    }}
                  >
                    取消
                  </Button>
                  <Button
                    onClick={handleCreateAttribute}
                    disabled={!newAttribute.displayName.trim() || !newAttribute.fieldName.trim() || createAttributeMutation.isPending}
                  >
                    {createAttributeMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        創建中...
                      </>
                    ) : (
                      "創建屬性"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAttributes.map((attr) => (
          <Card key={attr.id} className="transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-base">{attr.displayName}</CardTitle>
                  <p className="text-sm text-muted-foreground font-mono">
                    {attr.fieldName}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleEditClick(attr)}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() => handleDeleteClick(attr.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className={fieldTypeColors[attr.fieldType]}>
                    {fieldTypeLabels[attr.fieldType]}
                  </Badge>
                  {attr.isRequired && (
                    <Badge variant="outline" className="text-red-600 border-red-200">
                      必填
                    </Badge>
                  )}
                </div>
                
                {attr.options && attr.options.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">選項:</p>
                    <div className="flex flex-wrap gap-1">
                      {attr.options.map((option, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {option}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAttributes.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>找不到符合的產品屬性</p>
        </div>
      )}

      {/* Edit Attribute Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>編輯產品屬性</DialogTitle>
            <DialogDescription>
              修改產品屬性的設定
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="editDisplayName">顯示名稱</Label>
              <Input
                id="editDisplayName"
                value={editAttribute.displayName || ""}
                onChange={(e) => setEditAttribute({ ...editAttribute, displayName: e.target.value })}
                placeholder="例如: 顏色、材質類型"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editFieldName">欄位名稱 (自動生成)</Label>
              <Input
                id="editFieldName"
                value={editAttribute.fieldName || ""}
                onChange={(e) => {
                  setIsEditFieldNameManuallyEdited(true);
                  setEditAttribute({ ...editAttribute, fieldName: e.target.value });
                }}
                placeholder="例如: color, material_type"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editFieldType">欄位類型</Label>
              <Select
                value={editAttribute.fieldType}
                onValueChange={(value: FieldType) => setEditAttribute({ ...editAttribute, fieldType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(fieldTypeLabels).map(([value, label]) => (
                    <SelectItem 
                      key={value} 
                      value={value}
                      className="hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <div className={cn("w-2 h-2 rounded-full", fieldTypeColors[value as FieldType].split(' ')[0])} />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {[FieldType.SELECT, FieldType.MULTISELECT].includes(editAttribute.fieldType!) && (
              <div className="space-y-2">
                <Label>選項</Label>
                <div className="flex gap-2">
                  <Input
                    value={editOptionInput}
                    onChange={(e) => setEditOptionInput(e.target.value)}
                    placeholder="輸入選項"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addEditOption();
                      }
                    }}
                  />
                  <Button type="button" onClick={addEditOption}>
                    新增
                  </Button>
                </div>
                {editOptions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {editOptions.map((option, index) => (
                      <Badge key={index} variant="secondary" className="flex items-center gap-1">
                        {option}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={() => removeEditOption(index)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Checkbox
                id="editIsRequired"
                checked={editAttribute.isRequired}
                onCheckedChange={(checked) => setEditAttribute({ ...editAttribute, isRequired: checked as boolean })}
              />
              <Label htmlFor="editIsRequired">必填欄位</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                取消
              </Button>
              <Button
                onClick={handleUpdateAttribute}
                disabled={!editAttribute.displayName?.trim() || !editAttribute.fieldName?.trim() || updateAttributeMutation.isPending}
              >
                {updateAttributeMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    更新中...
                  </>
                ) : (
                  "更新"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>確認刪除</DialogTitle>
            <DialogDescription>
              您確定要刪除這個產品屬性嗎？此操作無法復原。
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteAttributeMutation.isPending}
            >
              {deleteAttributeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                "確認刪除"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}